import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  bildirimOkundu,
  bildirimleriGetir,
  okunmamisSayisi,
  OKUNMAMIS_DURUMLAR,
  tumunuOkundu,
  type BildirimOzeti,
} from '../api/bildirim';

/**
 * BİLDİRİM ROZETİ VE LİSTESİ.
 *
 * Rozet 60 saniyede bir tazelenir. Daha sık sorgulamak 3.000 kullanıcıda
 * anlamlı bir yük üretir; bildirimler saatlik işlerden doğduğu için
 * dakikalık gecikme kabul edilebilir. Web-push Faz 2'de eklenince bu
 * yoklama gereksizleşecek.
 */
const ROZET_TAZELEME_MS = 60_000;

export function BildirimRozeti() {
  const [acik, acikYaz] = useState(false);
  const kuyruk = useQueryClient();
  const gezin = useNavigate();

  const sayi = useQuery({
    queryKey: ['bildirim', 'sayi'],
    queryFn: okunmamisSayisi,
    refetchInterval: ROZET_TAZELEME_MS,
  });

  const liste = useQuery({
    queryKey: ['bildirim', 'liste'],
    queryFn: () => bildirimleriGetir(),
    // Panel açılışında tüm listeyi çekmek gereksiz; yalnız açılınca istenir.
    enabled: acik,
  });

  // `invalidateQueries` bir Promise döndürür; beklenmezse ESLint uyarır ve
  // daha önemlisi hata sessizce yutulur. Burada beklemek gerekmiyor,
  // tazeleme arka planda tamamlanabilir — bu yüzden Promise döndürülüyor.
  const tazele = (): Promise<void> => kuyruk.invalidateQueries({ queryKey: ['bildirim'] });

  const okundu = useMutation({
    mutationFn: bildirimOkundu,
    onSuccess: tazele,
  });

  const hepsiOkundu = useMutation({
    mutationFn: tumunuOkundu,
    onSuccess: tazele,
  });

  const adet = sayi.data?.adet ?? 0;

  const tikla = (bildirim: BildirimOzeti) => {
    if (OKUNMAMIS_DURUMLAR.includes(bildirim.durum)) okundu.mutate(bildirim.bildirimId);
    if (bildirim.hedefUrl) {
      acikYaz(false);
      // react-router 7'de `navigate` Promise döndürebiliyor; gezinme
      // sonucunu beklemeye gerek yok, `void` ile bilinçli olarak yutuluyor.
      void gezin(bildirim.hedefUrl);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => acikYaz((o) => !o)}
        className="relative rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-100"
      >
        Bildirimler
        {adet > 0 && (
          <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-xs font-medium text-white">
            {adet > 99 ? '99+' : adet}
          </span>
        )}
      </button>

      {acik && (
        <div className="absolute right-0 z-20 mt-2 w-96 max-w-[90vw] rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
            <span className="text-sm font-medium text-slate-900">Bildirimler</span>
            <button
              type="button"
              onClick={() => hepsiOkundu.mutate()}
              disabled={adet === 0 || hepsiOkundu.isPending}
              className="text-xs text-kurum-600 hover:underline disabled:text-slate-400 disabled:no-underline"
            >
              Tümünü okundu işaretle
            </button>
          </div>

          <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {liste.isPending && <li className="p-4 text-sm text-slate-500">Yükleniyor…</li>}
            {liste.data?.length === 0 && (
              <li className="p-4 text-sm text-slate-500">Bildiriminiz yok.</li>
            )}
            {liste.data?.map((bildirim) => (
              <li key={bildirim.bildirimId}>
                <button
                  type="button"
                  onClick={() => tikla(bildirim)}
                  className="block w-full px-4 py-3 text-left hover:bg-slate-50"
                >
                  <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                    {OKUNMAMIS_DURUMLAR.includes(bildirim.durum) && (
                      <span className="size-2 shrink-0 rounded-full bg-kurum-500" />
                    )}
                    {bildirim.baslik}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600">{bildirim.govde}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
